import React, { useState } from "react";

import { Box, Typography } from "@mui/material";
import { getPageMetrics } from "components/Report-generator/utils/pageMetrics";
import type {
  CanvasCard,
  PageLayout,
  ReportMeta,
  ReportOrientation,
  ReportPage
} from "store/types";

import { PAGE_LAYOUT_OPTIONS, PageView } from "./PageView";

interface Props {
  cards: CanvasCard[];
  pages: ReportPage[];
  selectedCardId: string;
  reportMeta: ReportMeta;
  orientation: ReportOrientation;
  isResizing: boolean;
  onResizingChange: (resizing: boolean) => void;
  onUpdateMeta: (updates: Partial<ReportMeta>) => void;
  onLogoFileChange: (file: File | null) => void;
  onSelectCard: (id: string) => void;
  onRemoveCard: (id: string) => void;
  onDuplicateCard: (id: string) => void;
  onUpdateCard: (id: string, updates: Partial<Omit<CanvasCard, "id" | "type">>) => void;
  onAddPage: (layout?: PageLayout) => void;
  onRemovePage: (page: number) => void;
  onUpdatePage: (pageIndex: number, updates: Partial<ReportPage>) => void;
}

export const Canvas: React.FC<Props> = ({
  cards,
  pages,
  selectedCardId,
  reportMeta,
  orientation,
  isResizing,
  onResizingChange,
  onUpdateMeta,
  onLogoFileChange,
  onSelectCard,
  onRemoveCard,
  onDuplicateCard,
  onUpdateCard,
  onAddPage,
  onRemovePage,
  onUpdatePage
}) => {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const pageCount = pages.length;
  const { width: pageWidth, height: pageHeight } = getPageMetrics(orientation);

  const groupedPages: CanvasCard[][] = Array.from({ length: pageCount }, (_, pageIndex) =>
    cards.filter((card) => card.page === pageIndex)
  );

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        overflowX: "auto",
        minHeight: 0,
        bgcolor: "#cfd8e8",
        p: "40px 32px 80px"
      }}
    >
      <Box
        sx={{
          width: "fit-content",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "32px"
        }}
      >
      {groupedPages.map((pageCards, pageIndex) => (
        <PageView
          key={pageIndex}
          pageIndex={pageIndex}
          pageCount={pageCount}
          page={pages[pageIndex]}
          pageCards={pageCards}
          allCards={cards}
          pageWidth={pageWidth}
          pageHeight={pageHeight}
          selectedCardId={selectedCardId}
          reportMeta={reportMeta}
          orientation={orientation}
          isResizing={isResizing}
          onResizingChange={onResizingChange}
          onUpdateMeta={onUpdateMeta}
          onLogoFileChange={onLogoFileChange}
          onSelectCard={onSelectCard}
          onRemoveCard={onRemoveCard}
          onDuplicateCard={onDuplicateCard}
          onUpdateCard={onUpdateCard}
          onRemovePage={() => onRemovePage(pageIndex)}
          onUpdatePage={(updates) => onUpdatePage(pageIndex, updates)}
        />
      ))}

      <Box sx={{ position: "relative", width: pageWidth, pb: 10 }}>
        <Box
          component="button"
          onClick={() => setShowAddMenu((v) => !v)}
          sx={{
            width: "100%",
            p: "18px 0",
            border: "2px dashed rgba(7,31,53,0.25)",
            borderRadius: "10px",
            bgcolor: "transparent",
            color: "rgba(7,31,53,0.55)",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer"
          }}
        >
          Add page
        </Box>
        {showAddMenu && (
          <Box
            sx={{
              position: "absolute",
              bottom: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              mb: 1,
              bgcolor: "#FFFFFF",
              borderRadius: "10px",
              boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
              border: "1px solid #dbe8f5",
              p: 1,
              display: "flex",
              gap: "6px",
              zIndex: 20,
              flexWrap: "wrap",
              justifyContent: "center"
            }}
          >
            {PAGE_LAYOUT_OPTIONS.map((option) => (
              <Box
                key={option.value}
                component="button"
                onClick={() => {
                  onAddPage(option.value);
                  setShowAddMenu(false);
                }}
                sx={{
                  p: "10px 14px",
                  border: "1px solid #dbe8f5",
                  borderRadius: "8px",
                  bgcolor: "#FAFCFE",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "4px",
                  minWidth: 92
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#071f35" }}>{option.tag}</Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#071f35" }}>{option.label}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      </Box>
    </Box>
  );
};
